import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { DragEvent } from 'react';
import { ChevronRight, GripVertical, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
// Removed shadcn sidebar imports - using custom components
import { NodeStruct, NodeCategory } from '@/types/node-types';
import { getNodeIconColor, getNodeStyling } from '@/constants/node-styling';
import { getIconComponent } from '@/pages/workflow/helper/icon-mapping';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { IconTile } from '../icon-tile';
import { GroupedToolNodeList } from './grouped-tool-node-list';
import {
  isNodeDeprecated,
  getDeprecationMessage,
  TOOLTIP_DELAY_MS,
} from '@/constants/deprecated-nodes';

interface NodeListProps {
  dataFilter: Record<string, NodeStruct[]>;
  sortedCategories: string[];
  openCategories: string[];
  setOpenCategories: (categories: string[]) => void;
  search: string;
  onDragStart?: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onAddNode?: (node: NodeStruct) => void;
  /** Click handler for selecting a node (used in popover mode where there's no drag) */
  onClick?: (node: NodeStruct) => void;
  onDragStartCategory?: (
    event: DragEvent<HTMLDivElement>,
    categoryName: string,
    nodes: NodeStruct[],
  ) => void;
  onAddCategory?: (categoryName: string, nodes: NodeStruct[]) => void;
  handleKeyDownInput?: (
    e: React.KeyboardEvent<HTMLElement>,
    name: string,
  ) => void;
  isLocked?: boolean;
  /** Index of the currently highlighted node for keyboard navigation */
  highlightedIndex?: number;
  /** Called with the full flat display order (matches rendered list, including Tool grouping) for keyboard selection */
  onFlatDisplayOrderChange?: (nodes: NodeStruct[]) => void;
  /** Check if a node is currently multi-selected */
  isNodeSelected?: (nodeId: string) => boolean;
  /** Returns the repeat count for a selected node (for duplicate badge) */
  getNodeSelectionCount?: (nodeId: string) => number;
  /** Toggle multi-selection for a node (triggered by Cmd/Ctrl+click) */
  onToggleSelect?: (node: NodeStruct) => void;
  /** Plain click: select a single node, clearing previous selection */
  onSelectSingle?: (node: NodeStruct) => void;
  /** Shift+click: range select from anchor to target */
  onShiftSelect?: (node: NodeStruct) => void;
  /** Double-click: immediately add the node, bypassing selection. */
  onDoubleClickAdd?: (node: NodeStruct) => void;
  /** Double-click on an MCP server row: immediately add all its sub-tools. */
  onDoubleClickAddCategory?: (nodes: NodeStruct[]) => void;
  /** Called when a drag operation ends (for clearing selection) */
  onDragEnd?: () => void;
  /** Called when an MCP server category row is clicked (selects all its nodes) */
  onSelectCategory?: (nodes: NodeStruct[]) => void;
  /** Additively add nodes to existing selection (used for Cmd/Ctrl+click on categories) */
  onAddToSelection?: (nodes: NodeStruct[]) => void;
  /** Range-select from anchor to target using a custom ordered list (used for Shift+click on categories) */
  onRangeSelect?: (targetNodeId: string, allOrderedNodeIds: string[]) => void;
  /** Initial state for tool subcategories within NodeList. Defaults to 'all-closed'. */
  initialSubCategoryState?: 'all-open' | 'all-closed' | string[];
  /** When set, only nodes with these sub-types are interactive; others are visually disabled */
  allowedSubTypes?: Set<string>;
}

export const NodeItem: React.FC<{
  node: NodeStruct;
  onDragStart?: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onDragEnd?: () => void;
  onAddNode?: (node: NodeStruct) => void;
  onClick?: (node: NodeStruct) => void;
  showGripIcon?: boolean;
  isLocked?: boolean;
  /** Contrasting row surface when rendered inside a muted container (e.g. error cards). */
  elevated?: boolean;
  /** Whether this node is currently multi-selected */
  selected?: boolean;
  /** How many copies of this node are selected (shown as badge when > 1) */
  selectionCount?: number;
  /** Callback to toggle multi-selection (triggered by Cmd/Ctrl+click) */
  onToggleSelect?: (node: NodeStruct) => void;
  /** Callback for plain click selection (select single node) */
  onSelectSingle?: (node: NodeStruct) => void;
  /** Callback for Shift+click range selection */
  onShiftSelect?: (node: NodeStruct) => void;
  /** Callback for double-click: immediately add the node (bypasses selection) */
  onDoubleClickAdd?: (node: NodeStruct) => void;
  /** Visual highlight for keyboard navigation (e.g. arrow keys in context menu) */
  isHighlighted?: boolean;
  /** Whether this specific node is disabled (e.g. workflow locked and not an allowed type) */
  nodeDisabled?: boolean;
}> = ({
  node,
  onDragStart,
  onDragEnd,
  onAddNode,
  onClick,
  showGripIcon = true,
  isLocked = false,
  elevated = false,
  selected = false,
  selectionCount = 0,
  onToggleSelect,
  onSelectSingle,
  onShiftSelect,
  onDoubleClickAdd,
  isHighlighted = false,
  nodeDisabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAddBtnHovered, setIsAddBtnHovered] = useState(false);
  const isEffectivelyLocked = isLocked || nodeDisabled;
  const isDraggable = Boolean(onDragStart) && !isEffectivelyLocked;
  const iconColor = getNodeIconColor(node.node_type, node.icon_color);
  const useThemeColors = iconColor === null;
  const isDeprecated = isNodeDeprecated(node.display_name);
  const deprecationMessage = getDeprecationMessage(node.display_name);
  const hasTooltipContent =
    (isDeprecated && deprecationMessage) ||
    node.user_config_description ||
    node.description;
  // bool for if we should show the smaller "Add ___ to canvas" tooltip
  const showAddBtnTooltip = Boolean(isAddBtnHovered && onAddNode);
  const hasAnyTooltip = Boolean(showAddBtnTooltip || hasTooltipContent);

  return (
    <Tooltip delayDuration={isAddBtnHovered ? 0 : TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'group flex w-full items-center rounded-xl text-left text-sm transition-all duration-200',
            elevated
              ? 'bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900'
              : 'bg-neutral-100 hover:bg-neutral-200 dark:hover:bg-neutral-900 dark:bg-neutral-800',
            isHovered &&
              (elevated
                ? 'bg-neutral-50 dark:bg-neutral-900'
                : 'bg-neutral-200'),
            isDraggable && 'cursor-grab active:cursor-grabbing',
            onClick && !isDraggable && 'cursor-pointer',
            isEffectivelyLocked && 'opacity-60 cursor-not-allowed',
            'gap-3 p-2',
            selected && 'ring-2 ring-primary/50',
            isHighlighted && 'ring-2 ring-primary',
          )}
          draggable={isDraggable}
          onDragStart={(e) => {
            // Modifier keys (shift/cmd/ctrl) indicate selection intent, not drag.
            // Without this, the browser starts a native HTML5 drag on any cursor
            // movement during the click, swallowing the click event entirely.
            if (e.shiftKey || e.metaKey || e.ctrlKey) {
              e.preventDefault();
              return;
            }
            onDragStart?.(e, node);
          }}
          onDragEnd={onDragEnd}
          onClick={(e) => {
            // When a double-click handler is wired, swallow the 2nd click of a
            // plain dblclick pair (e.detail === 2) so we don't toggle the
            // selection off right before onDoubleClick fires. Modifier keys
            // are reserved for selection actions (Cmd-toggle, Shift-range)
            // and should pass through unchanged so a Cmd+dblclick nets back
            // to the original selection state instead of leaking into add.
            if (
              onDoubleClickAdd &&
              e.detail >= 2 &&
              !e.shiftKey &&
              !e.metaKey &&
              !e.ctrlKey
            ) {
              e.stopPropagation();
              return;
            }
            if (e.shiftKey && onShiftSelect) {
              e.stopPropagation();
              onShiftSelect(node);
            } else if ((e.metaKey || e.ctrlKey) && onToggleSelect) {
              e.stopPropagation();
              onToggleSelect(node);
            } else if (onSelectSingle) {
              e.stopPropagation();
              onSelectSingle(node);
            } else {
              onClick?.(node);
            }
          }}
          onDoubleClick={(e) => {
            if (!onDoubleClickAdd || isEffectivelyLocked) return;
            // Modifier keys are for selection (Cmd / Shift); never trigger
            // an immediate add when one is held.
            if (e.shiftKey || e.metaKey || e.ctrlKey) return;
            e.stopPropagation();
            onDoubleClickAdd(node);
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <IconTile
            icon={node.icon}
            backgroundColor={iconColor || ''}
            iconColor={iconColor ? '#ffffff' : ''}
            size='small'
            className={cn(useThemeColors ? 'bg-black dark:bg-white' : '')}
            iconClassName={cn(
              useThemeColors ? 'text-white dark:text-black' : '',
              'w-5! h-5!',
            )}
          />
          <div className='flex-1 min-w-0 truncate'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-gray-900 truncate dark:text-white'>
                {node.user_config_name || node.display_name}
              </span>
              {selectionCount > 1 && (
                <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-white shrink-0'>
                  {selectionCount} copies
                </span>
              )}
              {isDeprecated ? (
                <Badge
                  variant='secondary'
                  className='text-xs shrink-0 text-red-600 bg-red-100'
                >
                  Deprecated
                </Badge>
              ) : (
                node.beta && (
                  <Badge
                    variant='secondary'
                    className='text-xs shrink-0 text-yellow-600 bg-yellow-100'
                  >
                    Beta
                  </Badge>
                )
              )}
            </div>
          </div>
          {onAddNode && (
            <div
              onMouseEnter={() => setIsAddBtnHovered(true)}
              onMouseLeave={() => setIsAddBtnHovered(false)}
              onFocus={() => setIsAddBtnHovered(true)}
              onBlur={() => setIsAddBtnHovered(false)}
            >
              <Button
                variant='ghost'
                size='sm'
                disabled={isEffectivelyLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isEffectivelyLocked) onAddNode(node);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
                className='opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded shrink-0 dark:text-white dark:hover:bg-neutral-800'
              >
                <Plus className='h-4 w-4 text-gray-600 dark:text-white' />
              </Button>
            </div>
          )}

          {showGripIcon && (
            <GripVertical className='h-4 w-4 text-gray-600 dark:text-white' />
          )}
        </div>
      </TooltipTrigger>
      {hasAnyTooltip && (
        <TooltipContent
          side='right'
          align={showAddBtnTooltip ? 'center' : 'start'}
          sideOffset={12}
          className='max-w-xs'
        >
          {showAddBtnTooltip ? (
            <p>
              {isEffectivelyLocked
                ? 'Workflow is locked'
                : `Add ${node.display_name} to canvas`}
            </p>
          ) : isDeprecated && deprecationMessage ? (
            <p>{deprecationMessage}</p>
          ) : (
            <p>
              {node.user_config_name || node.display_name} -{' '}
              {node.user_config_description || node.description}
            </p>
          )}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export const NodeList: React.FC<NodeListProps> = ({
  dataFilter,
  sortedCategories,
  openCategories,
  setOpenCategories,
  search,
  onDragStart,
  onAddNode,
  onClick,
  onDragStartCategory,
  onAddCategory,
  handleKeyDownInput,
  isLocked = false,
  highlightedIndex = -1,
  onFlatDisplayOrderChange,
  isNodeSelected,
  getNodeSelectionCount,
  onToggleSelect,
  onSelectSingle,
  onShiftSelect,
  onDoubleClickAdd,
  onDoubleClickAddCategory,
  onDragEnd,
  onSelectCategory,
  onAddToSelection,
  onRangeSelect,
  initialSubCategoryState = 'all-closed',
  allowedSubTypes,
}) => {
  const [toolCategoryDisplayOrder, setToolCategoryDisplayOrder] = useState<
    NodeStruct[] | null
  >(null);

  const handleCategoryToggle = useCallback(
    (category: string) => {
      setOpenCategories(
        openCategories.includes(category)
          ? [] // Close all categories if the clicked one is already open
          : [category], // Open only the clicked category
      );
    },
    [openCategories, setOpenCategories],
  );

  // Category offsets must match display order (Tool uses grouped/visible count when subcategories collapse)
  const { categoryOffsets } = useMemo(() => {
    const offsets: Record<string, number> = {};
    let currentOffset = 0;

    sortedCategories.forEach((category) => {
      const isExpanded = openCategories.includes(category) || search.length > 0;
      offsets[category] = currentOffset;

      if (isExpanded) {
        const nodes = dataFilter[category] || [];
        const count =
          category === NodeCategory.TOOL &&
          toolCategoryDisplayOrder?.length != null
            ? toolCategoryDisplayOrder.length
            : nodes.length;
        currentOffset += count;
      }
    });

    return { categoryOffsets: offsets };
  }, [
    sortedCategories,
    openCategories,
    search,
    dataFilter,
    toolCategoryDisplayOrder,
  ]);

  // Build full flat display order (same as rendered list; Tool category uses grouped order) and notify parent
  useEffect(() => {
    if (!onFlatDisplayOrderChange) return;
    const fullOrder: NodeStruct[] = [];
    sortedCategories.forEach((category) => {
      const isExpanded = openCategories.includes(category) || search.length > 0;
      if (!isExpanded) return;
      const nodes = dataFilter[category] || [];
      if (category === NodeCategory.TOOL && toolCategoryDisplayOrder?.length) {
        fullOrder.push(...toolCategoryDisplayOrder);
      } else {
        fullOrder.push(...nodes);
      }
    });
    onFlatDisplayOrderChange(fullOrder);
  }, [
    sortedCategories,
    openCategories,
    search,
    dataFilter,
    toolCategoryDisplayOrder,
    onFlatDisplayOrderChange,
  ]);

  return (
    // Node List grouped by category (node.node_type)
    <div className='space-y-4'>
      {sortedCategories.map((category) => {
        const nodes = dataFilter[category] || [];
        const styling = getNodeStyling(category as NodeCategory);
        const isExpanded =
          openCategories.includes(category) || search.length > 0;

        if (nodes.length === 0) return null;

        return (
          <div key={category} className='space-y-2 px-4'>
            <Button
              variant='ghost'
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryToggle(category);
              }}
              onKeyDown={(e) => handleKeyDownInput?.(e, category)}
              className={cn(
                'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2',
                'text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding]',
                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                'focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground',
                'disabled:pointer-events-none disabled:opacity-50',
                'group-has-[[data-sidebar=menu-action]]/menu-item:pr-8',
                'aria-disabled:pointer-events-none aria-disabled:opacity-50',
                'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground',
                'data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground',
                'group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2',
                '[&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
                'h-8 text-sm',
              )}
            >
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <div className='shrink-0'>{getIconComponent(styling.icon)}</div>
                <span className='font-medium text-sm truncate'>
                  {styling.label}
                </span>
              </div>
              <div
                className={cn(
                  'transform transition-transform duration-200 shrink-0',
                  isExpanded ? 'rotate-90' : 'rotate-0',
                )}
              >
                <ChevronRight className='size-4 shrink-0' />
              </div>
            </Button>

            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className='overflow-hidden'
                >
                  {category === NodeCategory.TOOL ? (
                    <GroupedToolNodeList
                      nodes={nodes}
                      onDragStart={onDragStart}
                      onAddNode={onAddNode}
                      onClick={onClick}
                      onDragStartCategory={onDragStartCategory}
                      onAddCategory={onAddCategory}
                      showGripIcon={Boolean(onDragStart)}
                      isLocked={isLocked}
                      isNodeSelected={isNodeSelected}
                      getNodeSelectionCount={getNodeSelectionCount}
                      onToggleSelect={onToggleSelect}
                      onSelectSingle={onSelectSingle}
                      onShiftSelect={onShiftSelect}
                      onDoubleClickAdd={onDoubleClickAdd}
                      onDoubleClickAddCategory={onDoubleClickAddCategory}
                      onDragEnd={onDragEnd}
                      onSelectCategory={onSelectCategory}
                      onAddToSelection={onAddToSelection}
                      onRangeSelect={onRangeSelect}
                      initialSubCategoryState={initialSubCategoryState}
                      highlightedIndex={
                        highlightedIndex >= 0
                          ? highlightedIndex - categoryOffsets[category]
                          : -1
                      }
                      highlightedIndexOffset={categoryOffsets[category]}
                      onFlatDisplayOrderChange={setToolCategoryDisplayOrder}
                    />
                  ) : (
                    <div className='space-y-2 p-0.5'>
                      {nodes.map((node, idx) => {
                        const globalIndex = categoryOffsets[category] + idx;
                        const isHighlighted = highlightedIndex === globalIndex;
                        return (
                          <div
                            key={node.display_name}
                            data-node-index={globalIndex}
                            className={cn(
                              isHighlighted && 'ring-2 ring-primary rounded-lg',
                            )}
                          >
                            <NodeItem
                              node={node}
                              onDragStart={onDragStart}
                              onDragEnd={onDragEnd}
                              onAddNode={onAddNode}
                              onClick={onClick}
                              showGripIcon={Boolean(onDragStart)}
                              isLocked={isLocked}
                              selected={isNodeSelected?.(node.node_id)}
                              selectionCount={getNodeSelectionCount?.(
                                node.node_id,
                              )}
                              onToggleSelect={onToggleSelect}
                              onSelectSingle={onSelectSingle}
                              onShiftSelect={onShiftSelect}
                              onDoubleClickAdd={onDoubleClickAdd}
                              nodeDisabled={
                                !!allowedSubTypes &&
                                !allowedSubTypes.has(node.node_sub_type)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
